import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './AppTile.css';

// AppTile Component - Individual app card with hover animations
// Opens external modules in new tabs with authentication tokens
// Displays app logo, name, type, and status with smooth motion effects
// Animation: Tiles fade in with scale, lift on hover, menu appears on hover

interface App {
  name: string;
  type: string;
  logo: string;
  status: string;
  category: string;
  moduleUrl?: string;
}

interface AppTileProps {
  app: App;
  index: number;
}

const AppTile: React.FC<AppTileProps> = ({ app }) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  // Handle tile click - open module in new tab
  const handleTileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // If app has moduleUrl, open it
    if (app.moduleUrl) {
      window.open(app.moduleUrl, '_blank');
    } else {
      console.log(`Module ${app.name} is not yet available`);
    }
  };

  // Tile animation variants
  const tileVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    },
    hover: {
      y: -4,
      boxShadow: '0 12px 24px rgba(0, 61, 130, 0.2)',
      transition: { duration: 0.3 }
    }
  };

  // Logo animation - bounce on hover
  const logoVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.4, delay: 0.1 }
    },
    hover: {
      scale: 1.15,
      rotate: 5,
      transition: { duration: 0.3 }
    }
  };

  // Menu button animation - fade in on hover
  const menuVariants = {
    hidden: { opacity: 0, x: 10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 }
    }
  };

  // Status badge animation
  const statusVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, delay: 0.2 }
    }
  };

  // NOTE: This tile connects to individual app microservices or opens external modules
  // Modules are external apps opened in new tabs with authentication tokens

  return (
    <motion.a
      href="#"
      className="app-tile"
      variants={tileVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={handleTileClick}
    >
      {/* More Options Menu */}
      <motion.button
        className="app-tile-menu"
        variants={menuVariants}
        animate={isHovered ? 'visible' : 'hidden'}
        onClick={(e) => {
          e.preventDefault();
          console.log(`Menu clicked for ${app.name}`);
        }}
      >
        ⋮
      </motion.button>

      {/* App Logo */}
      <motion.div
        className="app-logo"
        variants={logoVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
      >
        {app.logo}
      </motion.div>

      {/* App Name */}
      <div className="app-name">{app.name}</div>

      {/* App Type/Description */}
      <div className="app-type">{app.type}</div>

      {/* Status Badge */}
      <motion.div
        className="status-badge"
        variants={statusVariants}
        initial="hidden"
        animate="visible"
      >
        {app.status}
      </motion.div>
    </motion.a>
  );
};

export default AppTile;
